import { memo, useCallback, useState, useRef } from 'react';
import { memoizedSequence } from 'UIComponents/core/Functions';
import { MSG_TYPE_RESIZE_MINI_COMMENTER, MSG_TYPE_RESIZED_MINI_COMMENTER, MSG_TYPE_COMMENTS_BROADCAST, MSG_TYPE_CLOSE_MINI_COMMENTER, MSG_TYPE_MINI_COMMENTER_HAS_TEXT, MSG_TYPE_MINI_COMMENTER_HAS_NO_TEXT, MSG_TYPE_UPDATE_COLLABORATION_SIDEBAR_STATE, MSG_TYPE_SET_MINI_COMMENTER_MIN_HEIGHT_FOR_POPOVER, MSG_TYPE_MINI_COMMENTER_LOADED_COMMENTS } from 'collaboration-sidebar/constants/IFrameMessageTypes';
import { IFRAME_INIT_TIMEOUT } from 'collaboration-sidebar/constants/iframe';
import useHandleIFrameError from 'collaboration-sidebar/hooks/useHandleIFrameError';
import useCommentIndicatorsSync from 'collaboration-sidebar/features/deepCommenting/hooks/useCommentIndicatorsSync';
import useShowNotifications from 'collaboration-sidebar/hooks/useShowNotifications';
import { getEmbeddableMiniCommenterUrl } from 'collaboration-sidebar/utils/urls';
import AsyncIFrame from 'collaboration-sidebar/components/AsyncUIIFrame';
import MiniCommenterSkeleton from 'collaboration-sidebar/components/MiniCommenterSkeleton';
import useMiniCommenterSize from '../hooks/useMiniCommenterSize';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function EmbeddedCommentIframe({
  collaborationSidebarState,
  handleConfirmDeleteCommentModalMessage,
  moduleId,
  handleReady,
  setModuleReply,
  removeModuleReply,
  incrementReplyCountBy,
  broadcastCommentsMessage,
  popoverRef,
  autoFocus,
  onClose,
  closePopoverOnOutsideClickRef,
  metaDescription,
  moduleParentComment,
  onUpdateCollaborationSidebarState,
  userInfo,
  contentWrapperRef
}) {
  'use no memo';

  const sendMessageRef = useRef(undefined);
  const {
    miniCommenterSize,
    onResizeMiniCommenter,
    setMinHeightForPopover
  } = useMiniCommenterSize();
  const [isReady, setIsReady] = useState(false);
  const handleShowNotificationsMessage = useShowNotifications(true, sendMessageRef);
  const {
    objectType,
    objectId,
    ctaUrl,
    appName
  } = collaborationSidebarState;
  const {
    handleSidebarInitError,
    handleSidebarReadyError
  } = useHandleIFrameError({
    appName,
    context: 'mini-commenter'
  });
  const handleSetMinHeightForPopoverMessage = useCallback(({
    payload
  }) => {
    if (payload.type === MSG_TYPE_SET_MINI_COMMENTER_MIN_HEIGHT_FOR_POPOVER) {
      setMinHeightForPopover(payload.height);
    }
  }, [setMinHeightForPopover]);
  const handleMiniCommenterLoadedComments = useCallback(({
    payload
  }) => {
    if (payload.type === MSG_TYPE_MINI_COMMENTER_LOADED_COMMENTS) {
      setIsReady(true);
    }
  }, []);

  // Let the mini commenter load and report the true size of the comment list,
  // then we'll resize the iframe to fit it.
  const handleResizeIframeMessage = useCallback(({
    payload
  }) => {
    if (payload.type === MSG_TYPE_RESIZE_MINI_COMMENTER) {
      onResizeMiniCommenter(payload.height);
      if (sendMessageRef.current) {
        sendMessageRef.current(MSG_TYPE_RESIZED_MINI_COMMENTER);
      }
      // Reposition the popover incase it's placement changed
      // due to resizing the iframe
      setTimeout(() => {
        // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
        if (popoverRef.current) {
          // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
          popoverRef.current.reposition();
        }
      }, 0);
    }
  }, [popoverRef, onResizeMiniCommenter]);
  const handleCloseMessage = useCallback(({
    payload
  }) => {
    if (payload.type === MSG_TYPE_CLOSE_MINI_COMMENTER) {
      onClose();
    }
  }, [onClose]);
  const handleCommentsBroadcastMessage = useCallback(({
    payload
  }) => {
    if (payload.type === MSG_TYPE_COMMENTS_BROADCAST) {
      broadcastCommentsMessage(payload.broadcastPayload);
    }
  }, [broadcastCommentsMessage]);
  const handleHasTextMessage = useCallback(({
    payload
  }) => {
    if (payload.type === MSG_TYPE_MINI_COMMENTER_HAS_TEXT) {
      // React compiler does not like assigning to a ref passed as a prop
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      closePopoverOnOutsideClickRef.current = false;
    } else if (payload.type === MSG_TYPE_MINI_COMMENTER_HAS_NO_TEXT) {
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      closePopoverOnOutsideClickRef.current = true;
    }
  }, [closePopoverOnOutsideClickRef]);
  const handleUpdateCollaborationSidebarState = useCallback(({
    payload
  }) => {
    if (payload.type === MSG_TYPE_UPDATE_COLLABORATION_SIDEBAR_STATE) {
      onUpdateCollaborationSidebarState(payload.updates);
    }
  }, [onUpdateCollaborationSidebarState]);
  const {
    handleMessage: handleCommentMessage
  } = useCommentIndicatorsSync({
    setModuleReply,
    removeModuleReply,
    incrementReplyCountBy
  });
  const onMessage = memoizedSequence(handleResizeIframeMessage, handleMiniCommenterLoadedComments, handleSetMinHeightForPopoverMessage, handleConfirmDeleteCommentModalMessage, handleShowNotificationsMessage, handleCommentMessage, handleCommentsBroadcastMessage, handleCloseMessage, handleHasTextMessage, handleUpdateCollaborationSidebarState);
  return /*#__PURE__*/_jsxs("div", {
    ref: contentWrapperRef,
    children: [!isReady && /*#__PURE__*/_jsx(MiniCommenterSkeleton, {
      hasModuleParentComment: !!moduleParentComment
    }), /*#__PURE__*/_jsx(AsyncIFrame, {
      initTimeout: IFRAME_INIT_TIMEOUT,
      name: "mini-commenter",
      onInitError: handleSidebarInitError,
      onReady: ({
        sendMessage
      }) => {
        handleReady({
          sendMessage
        });
        sendMessageRef.current = sendMessage;
      },
      iframePassthruProps: {
        'data-test-id': 'mini-commenter-embedded-iframe',
        'data-iframe-ready': isReady,
        style: {
          height: `${isReady ? miniCommenterSize : 0}px`,
          width: '100%'
        }
      },
      embeddedPassthruProps: {
        metaDescription,
        moduleParentComment,
        collaborationSidebarState,
        userInfo
      },
      onReadyError: handleSidebarReadyError,
      onMessage: onMessage,
      src: getEmbeddableMiniCommenterUrl({
        objectType,
        objectId,
        ctaUrl,
        appName,
        moduleId,
        autoFocus,
        parentCommentId: moduleParentComment === null || moduleParentComment === void 0 ? void 0 : moduleParentComment.id
      })
    })]
  });
}
export default /*#__PURE__*/memo(EmbeddedCommentIframe);